<template>
    <AiWorkspaceShell>
        <template #rail>
            <div class="flex h-full min-h-0 flex-col">
                <div class="relative border-b border-gray-200 p-4">
                    <div class="flex items-center gap-2">
                        <TextInput
                            v-model="search"
                            class="flex-1"
                            :placeholder="t('vue.ai_content_search')"
                        />
                        <button
                            type="button"
                            class="relative flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition hover:bg-gray-50"
                            :class="{
                                'border-[#FF421E] text-[#FF421E]':
                                    selectedTagIds.length > 0,
                            }"
                            :title="t('vue.ai_tags_filter')"
                            @click="filterOpen = !filterOpen"
                        >
                            <FunnelIcon class="h-5 w-5" />
                            <span
                                v-if="selectedTagIds.length > 0"
                                class="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF421E] px-1 text-[10px] font-semibold text-white"
                            >
                                {{ selectedTagIds.length }}
                            </span>
                        </button>
                    </div>

                    <!-- Filter panel -->
                    <div
                        v-if="filterOpen"
                        class="absolute right-4 top-full z-10 mt-1 max-h-64 w-64 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
                    >
                        <p
                            v-if="filterableTags.length === 0"
                            class="px-2 py-2 text-xs text-gray-400"
                        >
                            {{ t("vue.ai_tags_none") }}
                        </p>
                        <label
                            v-for="tag in filterableTags"
                            :key="tag.id ?? tag.slug"
                            class="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 transition hover:bg-gray-50"
                        >
                            <input
                                type="checkbox"
                                :value="tag.id"
                                :checked="
                                    selectedTagIds.includes(tag.id as number)
                                "
                                class="h-4 w-4 rounded border-gray-300 text-[#FF421E] focus:ring-[#FF421E]"
                                @change="toggleFilter(tag.id as number)"
                            />
                            <span class="text-sm text-gray-700">{{
                                tag.name
                            }}</span>
                        </label>
                    </div>
                </div>

                <div class="flex-1 overflow-y-auto p-2">
                    <p
                        v-if="items.length === 0"
                        class="px-2 py-4 text-sm text-gray-400"
                    >
                        {{ t("vue.ai_content_empty") }}
                    </p>

                    <div v-for="section in sections" :key="section.key">
                        <p
                            v-if="
                                showSectionHeaders && section.items.length > 0
                            "
                            class="px-2 pb-1 pt-3 text-[11px] font-medium uppercase tracking-wide text-gray-400"
                        >
                            {{ section.label }}
                        </p>

                        <div
                            v-for="item in section.items"
                            :key="item.id"
                            class="group relative mb-1"
                        >
                            <div
                                v-if="editingId === item.id"
                                class="flex items-center gap-1 rounded-lg bg-gray-100 px-2 py-1.5"
                            >
                                <input
                                    v-model="editName"
                                    type="text"
                                    class="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-400 focus:outline-hidden"
                                    @keyup.enter="saveRename(item.id)"
                                    @keyup.esc="cancelRename"
                                />
                                <button
                                    type="button"
                                    class="shrink-0 cursor-pointer text-green-600 hover:text-green-700"
                                    @click="saveRename(item.id)"
                                >
                                    <CheckIcon class="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    class="shrink-0 cursor-pointer text-gray-400 hover:text-gray-600"
                                    @click="cancelRename"
                                >
                                    <XMarkIcon class="h-4 w-4" />
                                </button>
                            </div>

                            <template v-else>
                                <inertia-link
                                    :href="linkFor(item.id)"
                                    preserve-scroll
                                    preserve-state
                                    class="flex w-full items-center gap-2 rounded-lg py-2 pl-3 pr-14 text-left transition-colors"
                                    :class="
                                        selected && selected.id === item.id
                                            ? 'bg-gray-100 font-medium text-gray-800'
                                            : 'text-gray-600 hover:bg-gray-50'
                                    "
                                >
                                    <DocumentTextIcon
                                        class="h-4 w-4 shrink-0 text-gray-400"
                                    />
                                    <span
                                        class="min-w-0 flex-1 truncate text-sm"
                                    >
                                        {{ item.title }}
                                    </span>
                                </inertia-link>

                                <div
                                    class="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5"
                                >
                                    <button
                                        type="button"
                                        class="cursor-pointer rounded p-1 transition"
                                        :class="
                                            item.pinned
                                                ? 'text-[#FF421E]'
                                                : 'text-gray-400 opacity-0 hover:text-gray-700 group-hover:opacity-100'
                                        "
                                        :title="
                                            item.pinned
                                                ? t('vue.ai_chat_unpin')
                                                : t('vue.ai_chat_pin')
                                        "
                                        @click="togglePin(item.id)"
                                    >
                                        <Pin
                                            class="h-4 w-4"
                                            :class="
                                                item.pinned
                                                    ? 'fill-current'
                                                    : ''
                                            "
                                        />
                                    </button>
                                    <button
                                        type="button"
                                        class="cursor-pointer rounded p-1 transition hover:text-gray-700"
                                        :class="
                                            menuItem?.id === item.id
                                                ? 'text-gray-700 opacity-100'
                                                : 'text-gray-400 opacity-0 group-hover:opacity-100'
                                        "
                                        :title="t('vue.ai_chat_more_actions')"
                                        @click.stop.prevent="
                                            toggleMenu(item, $event)
                                        "
                                    >
                                        <EllipsisVerticalIcon class="h-4 w-4" />
                                    </button>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Row actions menu, teleported to the body so the list's own
                 vertical overflow never clips it. One row's menu open at a time. -->
            <Teleport to="body">
                <template v-if="menuItem !== null">
                    <div class="fixed inset-0 z-[590]" @click="closeMenu" />
                    <div
                        class="fixed z-[600] w-44 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                        :style="menuStyle"
                    >
                        <button
                            type="button"
                            class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-50"
                            @click="renameFromMenu"
                        >
                            <PencilSquareIcon class="h-4 w-4 shrink-0" />
                            {{ t("vue.ai_chat_rename") }}
                        </button>
                        <button
                            type="button"
                            class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm text-red-600 transition hover:bg-red-50"
                            @click="deleteFromMenu"
                        >
                            <TrashIcon class="h-4 w-4 shrink-0" />
                            {{ t("vue.ai_chat_delete") }}
                        </button>
                    </div>
                </template>
            </Teleport>
        </template>

        <template #main>
            <div
                class="mx-auto flex h-full min-h-0 w-full max-w-[800px] flex-col overflow-hidden"
            >
                <template v-if="selected">
                    <div
                        class="flex items-start justify-between gap-4 border-b border-gray-200 p-5"
                    >
                        <div class="min-w-0">
                            <div
                                v-if="editingDetail"
                                class="flex items-center gap-1"
                            >
                                <input
                                    v-model="detailName"
                                    type="text"
                                    class="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-400 focus:outline-hidden"
                                    @keyup.enter="saveDetail(selected.id)"
                                    @keyup.esc="editingDetail = false"
                                />
                                <button
                                    type="button"
                                    class="shrink-0 cursor-pointer text-green-600 hover:text-green-700"
                                    @click="saveDetail(selected.id)"
                                >
                                    <CheckIcon class="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    class="shrink-0 cursor-pointer text-gray-400 hover:text-gray-600"
                                    @click="editingDetail = false"
                                >
                                    <XMarkIcon class="h-4 w-4" />
                                </button>
                            </div>
                            <div v-else class="group flex items-center gap-2">
                                <h2
                                    class="truncate text-base font-semibold text-gray-800"
                                >
                                    {{ selected.title }}
                                </h2>
                                <button
                                    type="button"
                                    class="shrink-0 cursor-pointer text-gray-400 opacity-0 transition hover:text-gray-700 group-hover:opacity-100"
                                    :title="t('vue.ai_chat_rename')"
                                    @click="startDetail(selected)"
                                >
                                    <PencilSquareIcon class="h-4 w-4" />
                                </button>
                            </div>
                            <p class="mt-1 text-xs text-gray-400">
                                {{ formatDateTimeEu(selected.created_at) }}
                            </p>
                        </div>
                        <div class="flex shrink-0 items-center gap-3">
                            <inertia-link
                                v-if="selected.ai_chat_id"
                                :href="
                                    route('ai-chat.show', {
                                        chat: selected.ai_chat_id,
                                    })
                                "
                                class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50"
                            >
                                <ChatBubbleLeftRightIcon class="h-4 w-4" />
                                {{ t("vue.ai_content_open_chat") }}
                            </inertia-link>
                            <button
                                type="button"
                                class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                :disabled="emailing"
                                @click="emailDialogOpen = true"
                            >
                                <EnvelopeIcon class="h-4 w-4" />
                                {{ t("vue.ai_content_email") }}
                            </button>
                            <button
                                ref="exportButtonRef"
                                type="button"
                                class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50"
                                @click="toggleExportMenu"
                            >
                                <ArrowDownTrayIcon class="h-4 w-4" />
                                {{ t("vue.ai_chat_export") }}
                            </button>
                            <button
                                type="button"
                                class="cursor-pointer text-gray-400 transition hover:text-custom-alert-100"
                                @click="remove(selected.id)"
                            >
                                <TrashIcon class="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    <teleport to="body">
                        <template v-if="exportMenuOpen">
                            <div
                                class="fixed inset-0 z-[590]"
                                @click="exportMenuOpen = false"
                            />
                            <div
                                class="fixed z-[600] w-44 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
                                :style="exportMenuStyle"
                            >
                                <a
                                    v-for="option in exportOptions"
                                    :key="option.format"
                                    :href="
                                        exportHref(selected.id, option.format)
                                    "
                                    :target="
                                        option.newTab ? '_blank' : undefined
                                    "
                                    rel="noopener"
                                    class="block px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
                                    @click="exportMenuOpen = false"
                                >
                                    {{ option.label }}
                                </a>
                            </div>
                        </template>
                    </teleport>
                    <div class="flex-1 overflow-y-auto p-6">
                        <AiChatMarkdown :content="selected.content" />
                    </div>
                    <AiTagEditor
                        :entity-key="selected.id"
                        :submit-url="
                            route('ai-content.tags.update', {
                                savedContent: selected.id,
                            })
                        "
                        :tags="selected.tags"
                        :available-tags="availableTags"
                        collapsible
                    />

                    <AiEmailRecipientsDialog
                        :open="emailDialogOpen"
                        :sending="emailing"
                        :default-subject="`DMARCeye – ${selected.title}`"
                        @close="emailDialogOpen = false"
                        @send="sendEmail"
                    />
                </template>

                <div
                    v-else
                    class="flex h-full flex-col items-center justify-center gap-4 p-6 text-center"
                >
                    <AiChatRobot :size="92" />
                    <p class="text-sm text-gray-400">
                        {{ t("vue.ai_content_select") }}
                    </p>
                </div>
            </div>
        </template>
    </AiWorkspaceShell>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import {
    TrashIcon,
    FunnelIcon,
    ArrowDownTrayIcon,
    ChatBubbleLeftRightIcon,
    EnvelopeIcon,
    DocumentTextIcon,
    PencilSquareIcon,
    EllipsisVerticalIcon,
    CheckIcon,
    XMarkIcon,
} from "@heroicons/vue/24/outline";
import { Pin } from "lucide-vue-next";
import { useTranslations } from "@/composables/useTranslations";
import { formatDateTimeEu } from "@/utils/formatDateTimeEu";
import useConfirm from "@/composables/useConfirm";
import { create } from "@/composables/useNotifications";
import type { IAiTag } from "@/types/AiChat/IAiTag";

interface ISavedContentItem {
    id: number;
    title: string;
    created_at: string | null;
    pinned: boolean;
    tags: IAiTag[];
}

interface ISavedContentDetail {
    id: number;
    title: string;
    summary: string | null;
    content: string;
    created_at: string | null;
    ai_chat_id: number | null;
    tags: IAiTag[];
}

const props = defineProps<{
    items: ISavedContentItem[];
    q: string | null;
    activeTagIds: number[];
    availableTags: IAiTag[];
    selected: ISavedContentDetail | null;
}>();

const { t } = useTranslations();
const { show } = useConfirm();

// Split into Pinned / Recents; the server already returns pinned content first.
const sections = computed(() => [
    {
        key: "pinned",
        label: t("vue.ai_chat_pinned"),
        items: props.items.filter((item) => item.pinned),
    },
    {
        key: "recents",
        label: t("vue.ai_chat_recents"),
        items: props.items.filter((item) => !item.pinned),
    },
]);

const showSectionHeaders = computed(() =>
    props.items.some((item) => item.pinned),
);

const togglePin = (id: number): void => {
    router.post(
        route("ai-content.pin", { savedContent: id }),
        {},
        { preserveScroll: true, preserveState: true },
    );
};

const exportMenuOpen = ref(false);
const exportButtonRef = ref<HTMLElement | null>(null);
const exportMenuStyle = ref<Record<string, string>>({});

const exportOptions = computed(() => [
    { format: "pdf", label: t("vue.ai_chat_export_pdf"), newTab: true },
    { format: "html", label: t("vue.ai_chat_export_html"), newTab: true },
    { format: "json", label: t("vue.ai_chat_export_json"), newTab: false },
]);

const exportHref = (id: number, format: string): string =>
    route("ai-content.export", { savedContent: id, format });

const toggleExportMenu = (): void => {
    if (exportMenuOpen.value) {
        exportMenuOpen.value = false;
        return;
    }
    const rect = exportButtonRef.value?.getBoundingClientRect();
    if (rect) {
        exportMenuStyle.value = {
            top: `${rect.bottom + 4}px`,
            right: `${window.innerWidth - rect.right}px`,
        };
    }
    exportMenuOpen.value = true;
};

const search = ref(props.q ?? "");
const selectedTagIds = ref<number[]>([...props.activeTagIds]);
const filterOpen = ref(false);
let searchTimer: number | undefined;

const editingId = ref<number | null>(null);
const editName = ref("");

const startRename = (item: ISavedContentItem): void => {
    editingId.value = item.id;
    editName.value = item.title;
};

const cancelRename = (): void => {
    editingId.value = null;
    editName.value = "";
};

const saveRename = (id: number): void => {
    if (editName.value.trim() === "") {
        return;
    }
    router.put(
        route("ai-content.update", { savedContent: id }),
        { title: editName.value.trim() },
        { preserveScroll: true, preserveState: true, onSuccess: cancelRename },
    );
};

// Renaming from the open detail panel (separate state from the list rows).
const editingDetail = ref(false);
const detailName = ref("");

const startDetail = (content: ISavedContentDetail): void => {
    editingDetail.value = true;
    detailName.value = content.title;
};

const saveDetail = (id: number): void => {
    if (detailName.value.trim() === "") {
        return;
    }
    router.put(
        route("ai-content.update", { savedContent: id }),
        { title: detailName.value.trim() },
        {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => {
                editingDetail.value = false;
            },
        },
    );
};

// Only persisted team tags (with an id) can filter content.
const filterableTags = computed<IAiTag[]>(() =>
    props.availableTags.filter((tag) => tag.id !== null),
);

const visit = (): void => {
    router.get(
        route("ai-content.index"),
        {
            q: search.value,
            tags: selectedTagIds.value,
            content: props.selected?.id,
        },
        { preserveState: true, preserveScroll: true },
    );
};

const linkFor = (id: number): string =>
    route("ai-content.index", {
        content: id,
        q: search.value,
        tags: selectedTagIds.value,
    });

const toggleFilter = (id: number): void => {
    selectedTagIds.value = selectedTagIds.value.includes(id)
        ? selectedTagIds.value.filter((current) => current !== id)
        : [...selectedTagIds.value, id];
    visit();
};

watch(search, () => {
    if (searchTimer !== undefined) {
        window.clearTimeout(searchTimer);
    }
    searchTimer = window.setTimeout(visit, 300);
});

const remove = async (id: number): Promise<void> => {
    const confirmed = await show({
        header: t("vue.ai_chat_delete"),
        message: t("vue.ai_content_delete_confirm"),
        type: "danger",
        confirm: t("vue.ai_chat_delete"),
    });

    if (confirmed) {
        router.delete(route("ai-content.destroy", { savedContent: id }), {
            preserveScroll: true,
        });
    }
};

// The kebab menu is teleported to the body and positioned under its trigger, so
// the scrolling list never clips it. Only one row's menu is open at a time.
const menuItem = ref<ISavedContentItem | null>(null);
const menuStyle = ref<Record<string, string>>({});

const toggleMenu = (item: ISavedContentItem, event: MouseEvent): void => {
    if (menuItem.value?.id === item.id) {
        closeMenu();
        return;
    }
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    menuStyle.value = {
        top: `${rect.bottom + 4}px`,
        right: `${window.innerWidth - rect.right}px`,
    };
    menuItem.value = item;
};

const closeMenu = (): void => {
    menuItem.value = null;
};

const renameFromMenu = (): void => {
    const item = menuItem.value;
    closeMenu();
    if (item !== null) {
        startRename(item);
    }
};

const deleteFromMenu = (): void => {
    const item = menuItem.value;
    closeMenu();
    if (item !== null) {
        void remove(item.id);
    }
};

const emailing = ref(false);
const emailDialogOpen = ref(false);

const sendEmail = (payload: {
    recipientIds: number[];
    subject: string;
}): void => {
    if (props.selected === null) {
        return;
    }
    emailing.value = true;
    router.post(
        route("ai-content.email", { savedContent: props.selected.id }),
        { recipient_ids: payload.recipientIds, subject: payload.subject },
        {
            preserveScroll: true,
            onSuccess: () => {
                emailDialogOpen.value = false;
                create({ title: t("vue.ai_chat_email_sent"), type: "success" });
            },
            onError: () => {
                create({ title: t("vue.ai_chat_email_failed"), type: "error" });
            },
            onFinish: () => {
                emailing.value = false;
            },
        },
    );
};
</script>
